import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import { api, clearToken, setToken } from '@/lib/api';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

interface LoginResponse {
  success?: boolean;
  data?: {
    token?: string;
    user?: User;
  };
  token?: string;
  user?: User;
  message?: string;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      loading: false,
      error: null,

      async login(username, password) {
        set({ loading: true, error: null });
        try {
          const res = await api<LoginResponse>('auth/login.php', {
            method: 'POST',
            json: { username, password },
          });
          // The legacy backend nests under data.token / data.user
          const token = res.data?.token ?? res.token;
          const user = res.data?.user ?? res.user;
          if (!token || !user) throw new Error(res.message || 'เข้าสู่ระบบไม่สำเร็จ');
          setToken(token);
          set({ user, loading: false });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'เข้าสู่ระบบไม่สำเร็จ';
          set({ loading: false, error: msg });
          throw e;
        }
      },

      logout() {
        clearToken();
        set({ user: null });
      },

      async refresh() {
        try {
          const res = await api<{ data?: { user?: User }; user?: User }>(
            'auth/me.php'
          );
          const user = res.data?.user ?? res.user;
          if (user) set({ user });
        } catch {
          set({ user: null });
          clearToken();
        }
      },
    }),
    { name: 'baiboon_v8_auth', partialize: (s) => ({ user: s.user }) }
  )
);
